import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppError, AppErrorModel, EndpointType, RemoteServiceType } from '@libs/shared/models';
import { Observable, throwError } from 'rxjs';
import { MaintenanceService } from '../../maintenance';

export type GlobalRequestErrorHandlerFunction<T, S extends RemoteServiceType = RemoteServiceType, E extends EndpointType<S> = EndpointType<S>> =
  (response: HttpErrorResponse, service: S, endpoint: E, originalRequest: Observable<T>) => Observable<T>;

export interface GlobalRequestErrorHandler {
  key: string;
  status: number;
  serviceType: RemoteServiceType | undefined;
  errorHandlerFunction: GlobalRequestErrorHandlerFunction<unknown>;
}

export interface BffErrorModel {
  error: AppErrorModel;
}

@Injectable({ providedIn: 'root' })
export class ConnectorGlobalErrorHandlerService {
  private readonly maintenanceService: MaintenanceService = inject(MaintenanceService);
  private readonly errorHandlerList: GlobalRequestErrorHandler[] = [];

  public registerGlobalErrorHandler<T>(status: number, serviceType: RemoteServiceType | undefined, handler: GlobalRequestErrorHandlerFunction<T>): void {
    const key: string = status + '-' + serviceType;

    if (this.errorHandlerList.some(x => x.key === key)) {
      throw new Error(`Global error handler with key ${key} is already registered.`);
    }

    this.errorHandlerList.push({
      key,
      status,
      serviceType,
      errorHandlerFunction: handler as GlobalRequestErrorHandlerFunction<unknown>,
    });
  }

  public handleError<T, S extends RemoteServiceType = RemoteServiceType, E extends EndpointType<S> = EndpointType<S>>(
    response: HttpErrorResponse,
    service: S,
    endpoint: E,
    originalRequest: Observable<T>,
  ): Observable<T> {
    const specificHandler: GlobalRequestErrorHandler | undefined = this.errorHandlerList.find(entry => entry.status === response.status && entry.serviceType === service);
    const fallbackHandler: GlobalRequestErrorHandler | undefined = this.errorHandlerList.find(entry => entry.status === response.status && entry.serviceType === undefined);
    const handlerEntry: GlobalRequestErrorHandler | undefined = specificHandler ?? fallbackHandler;

    if (handlerEntry && !this.maintenanceService.maintenanceModeEnabled()) {
      return handlerEntry.errorHandlerFunction(response, service, endpoint, originalRequest) as Observable<T>;
    }

    return throwError(() => this.getAppError(response));
  }

  private getAppError(response: HttpErrorResponse): AppErrorModel {
    if (!response.error) {
      return AppError.create('Connector', response.status, response);
    }

    const bffError: BffErrorModel = response.error as BffErrorModel;

    if (!bffError.error) {
      return AppError.create('Connector', response.status, bffError);
    }

    return AppError.createFromModel(bffError.error);
  }
}
